import { InfoOutlined, OpenInNew } from '@mui/icons-material';
import type { PlayerProfileResponse } from '@on3/api';
import { Link } from '@on3/ui-lib/components/Link';
import { useAuth } from '@on3/ui-lib/index';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import clsx from 'clsx';
import { MobileMPU } from 'components/Ads/MobileMPU';
import { Vitals } from 'components/Athlete/Vitals';
import { CollegeDraft } from 'components/Db/Profile/College/CollegeDraft';
import { CollegeRanking } from 'components/Db/Profile/College/CollegeRanking';
import { CollegeTransfer } from 'components/Db/Profile/College/CollegeTransfer';
import { RecruitModule } from 'components/Db/Profile/College/RecruitModule';
import { PhotosThumbnailList } from 'components/Db/Profile/PhotosThumbnailList/PhotosThumbnailList';
import { PlayerContact } from 'components/Db/Profile/PlayerContact';
import { PlayerProfileTeamsList } from 'components/Db/Profile/PlayerProfileTeamsList/PlayerProfileTeamsList';
import { Rankings } from 'components/Db/Profile/Rankings';
import { ScoutingJournalModule } from 'components/Db/Profile/ScoutingJournal/ScoutingJournalModule';
import { PlayerVideosModule } from 'components/Db/Profile/Videos/PlayerVideosModule';
import { VisitCenter } from 'components/Db/Profile/VisitCenter';
import { DatabaseLayout } from 'components/Layouts/DatabaseLayout';
import { AdminLink } from 'components/User/AdminLink';
import useAthlete from 'contexts/AthleteProvider';
import { type PlayerProfilePageProps } from 'pages/rivals/[id]';
import { AthleteType } from 'utils/player';

import { AcademicsModule } from './Academics';
import { AthleteClaimBanner } from './AthleteClaim/AthleteClaimBanner';
import { PlayerBio } from './PlayerBio';
import { PlayerHeader } from './PlayerHeader';
import { PlayerInterestsModule } from './PlayerInterests/PlayerInterestsModule';
import { PlayerNewsModule } from './PlayerNews/PlayerNewsModule';
import styles from './PlayerProfile.module.scss';
import { PlayerSearch } from './PlayerSearch';
import { RemindUs } from './RemindUs/RemindUs';
import { PlayerStatsModule } from './Stats';

export const PlayerProfile = ({
  playerProfile,
  playerType,
  academics,
  college,
  contacts,
  news,
  nilValuation,
  photos,
  rankingsAll,
  rankingsPlayer,
  recruit,
  scoutingJournals,
  remindsUs,
  topTeams,
  topInterests,
  videos,
  visits,
  vitalAverages,
  vitals,
}: PlayerProfilePageProps) => {
  const { user } = useAuth();
  const { athlete, athleteInHighSchool, athleteInJuco, hasBaseProfile } =
    useAthlete();
  const { formatHref } = useDynamicRoute();

  const hasReminds = !!Object.values(remindsUs ?? {}).length;

  const { organizations } = playerProfile || {};

  const isTransfer = playerType === AthleteType.Transfer;

  const showManagedBy = !!athlete.managedByUser;
  const showRecruit = !!rankingsPlayer && !!recruit;
  const showRankings =
    !!rankingsAll &&
    !!rankingsPlayer &&
    !!playerProfile &&
    ![AthleteType.College, AthleteType.Transfer].includes(playerType);
  const showCollege = !!college && !isTransfer;
  const showTransfer = isTransfer && college && organizations;
  const showDrafted = !!playerProfile.draft?.key;
  const showTeamList = organizations && organizations?.length > 1;
  const showScoutingJournals = !!scoutingJournals?.list?.length || false;
  // const showScoutingJournals =
  //   !!scoutingJournals?.list?.length ||
  //   (hasBaseProfile && !athlete.athleteVerified);
  const showVideos = !!videos?.list?.length || hasBaseProfile;
  const showPhotos = !!photos?.list?.length;
  const showNews = !!news?.list?.length;
  const showVisits = !!visits?.visits?.length;
  const showVitals =
    (!!vitals?.playerMeasurements?.length && !!vitalAverages) ||
    (hasBaseProfile && !athlete.athleteVerified);
  const showTopTeams =
    (athleteInHighSchool || athleteInJuco || isTransfer) &&
    !!topTeams?.list?.length;
  const showTopInterests =
    ((athleteInHighSchool || athleteInJuco || isTransfer) &&
      !!topInterests?.list?.length) ||
    (hasBaseProfile && !athlete.athleteVerified);
  // const showHighSchoolStats = hasBaseProfile && !athlete.athleteVerified;
  const showHighSchoolStats = false;
  const showAcademics = hasBaseProfile && !athlete.athleteVerified;
  const recruitmentKey =
    isTransfer && athlete?.playerStatus?.recruitmentKey
      ? athlete?.playerStatus?.recruitmentKey
      : athlete?.recruitmentKey;

  return (
    <DatabaseLayout>
      <div
        className={styles.block}
        data-profile-type={playerType?.toLowerCase() || 'base'}
      >
        <AdminLink path={`/people/${athlete?.slug}/edit/`} />

        <section className={styles.playerDetail}>
          <header
            className={clsx({
              [styles.hasNavBar]: athlete.isAthlete && athlete.isCoach,
            })}
          >
            <PlayerSearch />
            <div className={styles.nameNavWrapper}>
              <h1>{athlete?.name}</h1>
              {athlete.isAthlete && athlete.isCoach && (
                <nav className={styles.coachesNav}>
                  <span className={clsx(styles.tab, styles.active)}>
                    Player Profile
                  </span>
                  <Link
                    className={styles.tab}
                    href={formatHref({
                      path: '/rivals/coach/[id]/',
                    })}
                  >
                    Coach Profile
                  </Link>
                </nav>
              )}
            </div>
          </header>
          <PlayerHeader
            player={
              {
                ...athlete,
                nilValue: nilValuation?.valuation, // tmp fix for player response
              } as PlayerProfileResponse
            }
          />
          <div className={styles.ncaaId}>
            <dl>
              <dt>NCAA ID:</dt>
              <dd>{athlete.ncaaId ?? '-'}</dd>
            </dl>
            {!!user?.pks?.length && !athlete.ncaaId && (
              <Link href="https://web3.ncaa.org/ecwr3/">
                Get your NCAA ID <OpenInNew />
              </Link>
            )}
          </div>
          {showManagedBy && (
            <div className={styles.managedProfile}>
              <InfoOutlined />
              <p>Verified Recruit Profile Managed by {athlete.name}.</p>
            </div>
          )}
        </section>

        <div className={styles.modules}>
          <AthleteClaimBanner />
          <div className={styles.top}>
            {showDrafted && (
              <div className={styles.draft}>
                <CollegeDraft playerProfile={playerProfile} />
              </div>
            )}

            {(showRankings || showCollege || showTransfer || showTeamList) && (
              <div className={styles.details}>
                {showTransfer && (
                  <div>
                    <CollegeTransfer
                      collegeData={college}
                      playerOrganizations={organizations}
                      rankingsAll={rankingsAll}
                    />
                  </div>
                )}
                {showCollege && (
                  <div>
                    <CollegeRanking {...college} />
                  </div>
                )}
                {showRankings && (
                  <div>
                    <Rankings
                      allRankingList={rankingsAll}
                      playerRankings={rankingsPlayer}
                    />
                  </div>
                )}
                {showTeamList && (
                  <div>
                    <PlayerProfileTeamsList
                      condensed
                      playerProfileData={playerProfile}
                    />
                  </div>
                )}
              </div>
            )}
            {showRecruit && (
              <div>
                <RecruitModule
                  playerRankings={rankingsPlayer}
                  recruitData={recruit}
                />
              </div>
            )}
            {showTopInterests && (
              <div className={styles.topInterests}>
                <h2>Top Teams</h2>
                <PlayerInterestsModule
                  list={topInterests?.list?.slice(0, 5)}
                  playerType={playerType}
                  recruitmentKey={recruitmentKey}
                />
              </div>
            )}
            {showVideos && (
              <div className={styles.videos}>
                <PlayerVideosModule videoData={videos} />
              </div>
            )}
          </div>

          <div className={styles.middle}>
            {showHighSchoolStats && <PlayerStatsModule player={athlete} />}
            {showTopTeams && (
              <section className={styles.topTeams}>
                <h2>Top Teams</h2>
                <PlayerInterestsModule
                  list={topTeams.list?.slice(0, 5)}
                  recruitmentKey={recruitmentKey}
                />
              </section>
            )}
          </div>

          <div className={styles.bottom}>
            <div>
              <PlayerBio description={athlete?.description} />
              {showScoutingJournals && (
                <section className={styles.scouting}>
                  <h2>Scouting</h2>
                  <ScoutingJournalModule
                    list={scoutingJournals?.list ?? []}
                    pagination={scoutingJournals?.pagination}
                  />
                </section>
              )}
              {showNews && (
                <div>
                  <PlayerNewsModule
                    recruitmentKey={recruitmentKey}
                    recruitsArticles={news}
                  />
                </div>
              )}
              <MobileMPU id="mobile_mpu_middle" incontent />
              {showVitals && (
                <section className={styles.vitals}>
                  <h2>Measurements & Testing</h2>
                  <Vitals
                    averages={vitalAverages}
                    measurements={(vitals?.playerMeasurements ?? [])
                      .sort((a, b) => b.key - a.key)
                      ?.filter(
                        (m, i, arr) =>
                          arr.findIndex(
                            (v) =>
                              v.measurementTypeKey === m.measurementTypeKey,
                          ) === i,
                      )
                      ?.slice(0, 6)}
                    player={athlete ?? ({} as PlayerProfileResponse)}
                  />
                </section>
              )}
              {showPhotos && (
                <section className={styles.photos}>
                  <h2>Photos</h2>
                  <PhotosThumbnailList
                    numberOfPhotos={10}
                    playerImages={photos}
                  />
                </section>
              )}
            </div>

            <div>
              {showAcademics && <AcademicsModule academics={academics} />}
              <PlayerContact
                contactInfo={contacts}
                player={athlete as PlayerProfileResponse}
              />
              {showVisits && (
                <section className={styles.visits}>
                  <h2>Visit Center</h2>
                  <VisitCenter visitCenter={visits} />
                </section>
              )}
              {hasReminds && (
                <div>
                  <RemindUs {...remindsUs} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DatabaseLayout>
  );
};
